import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  deleteOpportunityDocument,
  listOpportunityDocuments,
  opportunityDocumentFileUrl,
  uploadOpportunityDocument,
} from "../../api/clientOpportunityDocumentsApi";
import { formatClientDateTime } from "../../utils/clientPortalFormat";
import "../../styles/clientPortalUploads.css";

function formatFileSize(bytes) {
  if (!bytes) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isPdf(doc) {
  if (!doc) return false;
  if (String(doc.mimeType || "").toLowerCase().includes("pdf")) return true;
  return /\.pdf$/i.test(String(doc.fileName || doc.fileUrl || ""));
}

function isImage(doc) {
  if (!doc) return false;
  if (String(doc.mimeType || "").toLowerCase().startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp)$/i.test(String(doc.fileName || doc.fileUrl || ""));
}

function DocumentPreviewModal({ clientViewId, opportunityId, document: doc, onClose }) {
  const titleId = useId();
  const sourceUrl = doc
    ? opportunityDocumentFileUrl(clientViewId, opportunityId, doc.documentId)
    : "";
  const downloadUrl = doc
    ? opportunityDocumentFileUrl(clientViewId, opportunityId, doc.documentId, { download: true })
    : "";
  const pdf = isPdf(doc);
  const image = isImage(doc);
  const [preview, setPreview] = useState({ status: "idle", url: "", error: "" });

  useEffect(() => {
    if (!doc) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doc, onClose]);

  useEffect(() => {
    if (!doc || !sourceUrl) {
      setPreview({ status: "idle", url: "", error: "" });
      return undefined;
    }
    let cancelled = false;
    let objectUrl = "";
    setPreview({ status: "loading", url: "", error: "" });
    fetch(sourceUrl, { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => null);
          throw new Error(body?.message || "Unable to load this file.");
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreview({ status: "ready", url: objectUrl, error: "" });
      })
      .catch((error) => {
        if (!cancelled) {
          setPreview({
            status: "error",
            url: "",
            error: error.message || "Unable to load this file.",
          });
        }
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc?.documentId, sourceUrl]);

  if (!doc) return null;

  return (
    <div className="cp-document-modal-root" role="presentation" onClick={onClose}>
      <div
        className="cp-document-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cp-document-modal__header">
          <div>
            <p className="cp-document-modal__kicker">Attachment</p>
            <h2 id={titleId}>{doc.fileName || "Document"}</h2>
          </div>
          <div className="cp-document-modal__actions">
            {preview.status !== "error" ? (
              <>
                <a className="btn btn-secondary" href={sourceUrl} target="_blank" rel="noreferrer">
                  Open in new tab
                </a>
                <a className="btn btn-secondary" href={downloadUrl} target="_blank" rel="noreferrer">
                  Download
                </a>
              </>
            ) : null}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </header>
        <div className="cp-document-modal__body">
          {preview.status === "loading" || preview.status === "idle" ? (
            <p className="muted">Loading attachment…</p>
          ) : preview.status === "error" ? (
            <p className="cp-upload-error">{preview.error}</p>
          ) : pdf ? (
            <iframe title={doc.fileName || "Attachment"} src={preview.url} className="cp-document-modal__frame" />
          ) : image ? (
            <img src={preview.url} alt={doc.fileName || "Attachment"} className="cp-document-modal__image" />
          ) : (
            <div>
              <p className="muted">
                This file type can’t be previewed here. Open it in a new tab or download the original.
              </p>
              <a className="btn btn-primary" href={downloadUrl} target="_blank" rel="noreferrer">
                Download {doc.fileName || "file"}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OpportunityDocumentsPanel({
  clientViewId,
  opportunityId,
  canEdit,
  intro = "Attach contracts, disclosures, HOA packets, and other files.",
}) {
  const inputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await listOpportunityDocuments(clientViewId, opportunityId);
      setDocuments(body.documents || []);
    } catch (err) {
      setError(err.message || "Unable to load documents.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, opportunityId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    setError("");
    try {
      await uploadOpportunityDocument(clientViewId, opportunityId, file);
      await load();
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Remove ${doc.fileName}?`)) {
      return;
    }
    try {
      await deleteOpportunityDocument(clientViewId, opportunityId, doc.documentId);
      if (previewDoc?.documentId === doc.documentId) setPreviewDoc(null);
      await load();
    } catch (err) {
      setError(err.message || "Unable to remove document.");
    }
  };

  return (
    <section className="panel-card cp-documents-panel">
      <div className="cp-documents-header">
        <div>
          <h3 className="opp-section-heading">Documents</h3>
          <p className="muted">{intro}</p>
        </div>
        {canEdit ? (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? "Uploading…" : "+ Upload document"}
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        className="visually-hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/jpeg,image/png,image/webp"
        onChange={handleUpload}
      />

      {error ? <p className="cp-upload-error">{error}</p> : null}
      {loading ? <p className="muted">Loading documents…</p> : null}

      {!loading && documents.length === 0 ? (
        <p className="muted">No documents attached yet.</p>
      ) : null}

      {!loading && documents.length > 0 ? (
        <ul className="cp-documents-list">
          {documents.map((doc) => (
            <li key={doc.documentId} className="cp-document-item">
              <div>
                <button
                  type="button"
                  className="cp-document-name"
                  onClick={() => setPreviewDoc(doc)}
                >
                  {doc.fileName}
                </button>
                <div className="muted cp-document-meta">
                  {formatClientDateTime(doc.createdAt)}
                  {doc.fileSize ? ` · ${formatFileSize(doc.fileSize)}` : ""}
                  {doc.uploadedByName ? ` · ${doc.uploadedByName}` : ""}
                </div>
              </div>
              <div className="cp-document-item__actions">
                <button type="button" className="btn btn-secondary" onClick={() => setPreviewDoc(doc)}>
                  View
                </button>
                {canEdit ? (
                  <button
                    type="button"
                    className="cv-button-link cp-settings-danger-link"
                    onClick={() => handleDelete(doc)}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <DocumentPreviewModal
        clientViewId={clientViewId}
        opportunityId={opportunityId}
        document={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </section>
  );
}

export default OpportunityDocumentsPanel;
